/**
 * Cliente para el endpoint POST /ai/chat con respuesta SSE.
 *
 * El SDK no usa EventSource porque éste solo soporta GET; usamos fetch + ReadableStream
 * y un parser de eventos `event:` + `data:`.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

import type { ChatReference } from '@toga/shared'
import { getCsrfToken, notifyUnauthorized } from './api'

export type AiChatEvent =
  | { type: 'start'; conversationId: string; userMessageId: string }
  | { type: 'delta'; text: string }
  | {
      type: 'done'
      conversationId: string
      stopReason: string | null
      usage: {
        inputTokens: number
        outputTokens: number
        cacheCreationInputTokens: number
        cacheReadInputTokens: number
      }
      references: ChatReference[]
    }
  | { type: 'error'; message: string }

export interface ChatStreamRequest {
  conversationId?: string
  message: string
  contextType?: 'NONE' | 'OPPORTUNITY' | 'CLIENT' | 'DOCUMENT'
  contextId?: string
}

/**
 * Abre la conexión SSE y va invocando onEvent con cada evento del stream.
 * Devuelve una función para abortar.
 */
export async function streamChat(
  req: ChatStreamRequest,
  onEvent: (e: AiChatEvent) => void,
  abortSignal?: AbortSignal
): Promise<void> {
  // /ai/chat es POST → requiere token CSRF (F14). Lo añadimos aquí porque este
  // fetch de streaming no pasa por el helper api.ts.
  const csrf = await getCsrfToken()
  const res = await fetch(`${API_BASE}/ai/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-CSRF-Token': csrf,
    },
    body: JSON.stringify(req),
    signal: abortSignal,
  })

  if (!res.ok || !res.body) {
    if (res.status === 401) notifyUnauthorized()
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = await res.text().catch(() => '')
    }
    const message =
      (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
        ? body.error
        : null) ?? `Error ${res.status} al conectar con la IA`
    onEvent({ type: 'error', message })
    return
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buf = ''

  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })

    let idx: number
    // Cada bloque SSE termina en doble salto
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const chunk = buf.slice(0, idx)
      buf = buf.slice(idx + 2)
      const evt = parseSseChunk(chunk)
      if (evt) onEvent(evt)
    }
  }
}

function parseSseChunk(chunk: string): AiChatEvent | null {
  // Líneas que empiezan con ':' son comentarios/heartbeats
  const lines = chunk.split('\n').filter((l) => l && !l.startsWith(':'))
  let event = 'message'
  const dataParts: string[] = []
  for (const line of lines) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataParts.push(line.slice(5).trim())
  }
  if (dataParts.length === 0) return null
  const data = dataParts.join('\n')
  try {
    const parsed = JSON.parse(data)
    return { type: event, ...parsed } as AiChatEvent
  } catch {
    return { type: 'error', message: 'Respuesta malformada del servidor' }
  }
}
